// File: packages/chatbot/src/utils/memory.ts
//
// This utility handles the persistence of transient chat sessions (i.e., new chats
// that haven't been saved to the database yet) in the browser's storage.

import type { UIMessage } from '../types';

/**
 * Defines the scope for chat memory to prevent collisions.
 * 'site': For a global, session-wide assistant (like on the homepage). Uses sessionStorage.
 * 'agent': For a specific agent page. Uses localStorage with a TTL.
 */
export type ChatMemoryScope = 'site' | 'agent';

/**
 * Generates a unique storage key based on the scope and an optional agent ID.
 */
function getMemoryKey(scope: ChatMemoryScope, agentId?: string): string {
  if (scope === 'agent' && !agentId) {
    console.warn("Agent memory scope requires an agentId.");
    return 'dopo:chat:transient:default-agent';
  }
  return scope === 'site' 
    ? `dopo:chat:transient:site` 
    : `dopo:chat:transient:agent:${agentId}`;
}

/**
 * Saves the current message history to the appropriate browser storage.
 *
 * @param messages The array of UIMessages to save.
 * @param scope The memory scope ('site' or 'agent').
 * @param agentId The agent's ID, required for 'agent' scope.
 * @param ttlDays The Time-To-Live in days for 'agent' scope (localStorage).
 */
export function saveChatMemory(messages: UIMessage[], scope: ChatMemoryScope, agentId?: string, ttlDays = 7): void {
  if (typeof window === 'undefined' || messages.length === 0) return;

  const key = getMemoryKey(scope, agentId);
  const storage = scope === 'site' ? sessionStorage : localStorage;
  
  const payload = {
    messages,
    // Add an expiry timestamp only for localStorage
    ...(scope === 'agent' && { expiresAt: Date.now() + ttlDays * 24 * 60 * 60 * 1000 }),
  };

  try {
    storage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.error("Failed to save chat memory:", error);
  }
}

/**
 * Loads chat history from browser storage if it exists and is not expired.
 *
 * @param scope The memory scope ('site' or 'agent').
 * @param agentId The agent's ID, required for 'agent' scope.
 * @returns An array of UIMessages or null if not found or expired.
 */
export function loadChatMemory(scope: ChatMemoryScope, agentId?: string): UIMessage[] | null {
  if (typeof window === 'undefined') return null;

  const key = getMemoryKey(scope, agentId);
  const storage = scope === 'site' ? sessionStorage : localStorage;
  const raw = storage.getItem(key);
  if (!raw) return null;

  try {
    const data = JSON.parse(raw);
    
    // Check for expiration if the payload has an 'expiresAt' property
    if (data.expiresAt && Date.now() > data.expiresAt) {
      storage.removeItem(key);
      return null;
    }

    return Array.isArray(data.messages) ? data.messages : null;
  } catch {
    storage.removeItem(key); // Clear corrupted data
    return null;
  }
}

/**
 * Clears the chat history for a given scope from browser storage.
 *
 * @param scope The memory scope ('site' or 'agent').
 * @param agentId The agent's ID, required for 'agent' scope.
 */
export function clearChatMemory(scope: ChatMemoryScope, agentId?: string): void {
  if (typeof window === 'undefined') return;
  const key = getMemoryKey(scope, agentId);
  const storage = scope === 'site' ? sessionStorage : localStorage;
  storage.removeItem(key);
}